"use client";

import * as React from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const thumbnails = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_1.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_1.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_1.webp-wvc-sizes",
  alt: "PU Sandwich Panel core detail"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_2.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_2.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_2.webp-wvc-sizes",
  alt: "PU Sandwich Panel joint system"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_3.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_3.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_3.png-wvc-sizes",
  alt: "PU Sandwich Panel surface finish"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_4.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_4.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_detail_4.webp-wvc-sizes",
  alt: "PU Sandwich Panel installation"
}];
const specifications = [{
  key: "Core Material",
  value: "Polyurethane (PU)"
}, {
  key: "Thickness Range",
  value: "50mm - 150mm"
}, {
  key: "Thermal Conductivity",
  value: "0.022 W/mK"
}, {
  key: "Fire Rating",
  value: "B1 / B2 Class"
}, {
  key: "Density",
  value: "40-42 kg/m³"
}];
const features = [{
  title: "Overview",
  description: "High-performance structural panels designed for superior thermal efficiency in industrial and commercial buildings."
}, {
  title: "Key Advantages",
  description: "Exceptional insulation, lightweight construction, rapid installation, and high load-bearing capacity."
}, {
  title: "Applications",
  description: "Cold storage, food processing plants, warehouses, and energy-efficient residential projects."
}, {
  title: "Technical Specifications",
  description: "Available in various RAL colors with galvanized steel skins and high-density PU foam core."
}];
function FeaturedBladeConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const [activeThumb, setActiveThumb] = React.useState(0);
  const [activeImage, setActiveImage] = React.useState({
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_hero.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_hero.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/pu_panel_hero.webp-wvc-sizes"
  });
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div className="aspect-[4/5] rounded-lg bg-muted/40 animate-pulse" />
        <div className="space-y-6">
          <div className="h-4 w-48 bg-muted/40 animate-pulse rounded-md" />
          <div className="h-16 w-full bg-muted/40 animate-pulse rounded-md" />
          <div className="h-64 w-full bg-muted/40 animate-pulse rounded-md" />
        </div>
      </div>;
  }
  if (error || products.length === 0) {
    return <FeaturedBladeStatic thumbnails={thumbnails} activeImage={activeImage} setActiveImage={setActiveImage} activeThumb={activeThumb} setActiveThumb={setActiveThumb} />;
  }
  const product = products[0];
  const heroImage = product.featured_image_url || activeImage.src;
  return <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
      <div className="lg:pl-10 lg:pt-10">
        <div className="relative overflow-hidden rounded-lg shadow-md bg-muted">
          <img src={heroImage} alt={product.title} loading="lazy" decoding="async" className="w-full h-auto aspect-[4/5] object-cover" />
        </div>

        <div className="flex items-center gap-4 mt-6">
          {thumbnails.map((thumb, i) => <Button key={i} data-index={i} onClick={() => {
          setActiveThumb(i);
          setActiveImage({
            src: thumb.src,
            srcset: thumb.srcset,
            sizes: thumb.sizes
          });
        }} variant="ghost" className={`p-0 h-auto w-[60px] overflow-hidden rounded-lg transition-all ${activeThumb === i ? "outline outline-2 outline-offset-2 outline-primary" : "opacity-70 hover:opacity-100"}`} aria-label={`View ${thumb.alt}`}>
              <img src={thumb.src} data-wvc-srcset={thumb.srcset} data-wvc-sizes={thumb.sizes} alt={thumb.alt} loading="lazy" decoding="async" className="w-[60px] h-[60px] object-cover" />
            </Button>)}
        </div>
      </div>

      <div className="lg:pl-20 flex flex-col justify-center">
        <p className="font-default text-xs tracking-widest uppercase text-primary mb-6">
          PU Sandwich Panels
        </p>

        <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-tight text-foreground">
          PU Sandwich <em className="italic">Panels</em>
        </h2>

        <Separator className="my-8 bg-border" />

        <dl className="space-y-0">
          {specifications.map((spec, i) => <div key={i} data-index={i} className="flex items-baseline justify-between py-3 border-b border-border/60">
              <dt className="font-default text-sm text-muted-foreground">
                {spec.key}
              </dt>
              <dd className="font-default text-sm text-foreground">
                {spec.value}
              </dd>
            </div>)}
        </dl>

        <div className="mt-10 space-y-8">
          {features.map((feature, i) => <div key={i} data-index={i} className="space-y-2">
              <h3 className="font-default font-bold text-lg text-foreground uppercase tracking-tight">
                {feature.title}
              </h3>
              <p className="font-default text-base leading-relaxed text-foreground/85">
                {feature.description}
              </p>
            </div>)}
        </div>

        <div className="mt-10 flex items-baseline gap-4">
          <span className="font-default text-5xl md:text-6xl font-bold text-foreground">
            ${product.price.toFixed(0)}
          </span>
          {product.discounted_price !== undefined && product.discounted_price > 0 && <span className="font-default text-xl text-muted-foreground line-through">
                ${product.discounted_price.toFixed(0)}
              </span>}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full py-5 text-base rounded-lg shadow-md" text="Add to Cart" showQuantity={false} />
          <Link to={product.link} className="sm:w-auto w-full">
            <Button variant="outline" className="w-full py-5 text-base border-primary text-primary hover:bg-primary/5 rounded-lg">
              View Details →
            </Button>
          </Link>
        </div>

        <p className="mt-8 font-default italic text-sm text-primary">
          Direct sourcing from certified manufacturers.
        </p>
      </div>
    </div>;
}
function FeaturedBladeStatic({
  thumbnails,
  activeImage,
  setActiveImage,
  activeThumb,
  setActiveThumb
}: {
  thumbnails: typeof thumbnails;
  activeImage: {
    src: string;
    srcset: string;
    sizes: string;
  };
  setActiveImage: (img: {
    src: string;
    srcset: string;
    sizes: string;
  }) => void;
  activeThumb: number;
  setActiveThumb: (i: number) => void;
}) {
  return <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
      <div className="lg:pl-10 lg:pt-10">
        <div className="relative overflow-hidden rounded-lg shadow-md bg-muted">
          <img src={activeImage.src} data-wvc-srcset={activeImage.srcset} data-wvc-sizes={activeImage.sizes} alt="PU Sandwich Panel featured" loading="lazy" decoding="async" className="w-full h-auto aspect-[4/5] object-cover" />
        </div>
        <div className="flex items-center gap-4 mt-6">
          {thumbnails.map((thumb, i) => <Button key={i} data-index={i} onClick={() => {
          setActiveThumb(i);
          setActiveImage({
            src: thumb.src,
            srcset: thumb.srcset,
            sizes: thumb.sizes
          });
        }} variant="ghost" className={`p-0 h-auto w-[60px] overflow-hidden rounded-lg transition-all ${activeThumb === i ? "outline outline-2 outline-offset-2 outline-primary" : "opacity-70 hover:opacity-100"}`} aria-label={`View ${thumb.alt}`}>
              <img src={thumb.src} data-wvc-srcset={thumb.srcset} data-wvc-sizes={thumb.sizes} alt={thumb.alt} loading="lazy" decoding="async" className="w-[60px] h-[60px] object-cover" />
            </Button>)}
        </div>
      </div>

      <div className="lg:pl-20 flex flex-col justify-center">
        <p className="font-default text-xs tracking-widest uppercase text-primary mb-6">
          PU Sandwich Panels
        </p>
        <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-tight text-foreground">
          PU Sandwich <em className="italic">Panels</em>
        </h2>
        <Separator className="my-8 bg-border" />
        <dl className="space-y-0">
          {specifications.map((spec, i) => <div key={i} data-index={i} className="flex items-baseline justify-between py-3 border-b border-border/60">
              <dt className="font-default text-sm text-muted-foreground">
                {spec.key}
              </dt>
              <dd className="font-default text-sm text-foreground">
                {spec.value}
              </dd>
            </div>)}
        </dl>
        
        <div className="mt-10 space-y-8">
          {features.map((feature, i) => <div key={i} data-index={i} className="space-y-2">
              <h3 className="font-default font-bold text-lg text-foreground uppercase tracking-tight">
                {feature.title}
              </h3>
              <p className="font-default text-base leading-relaxed text-foreground/85">
                {feature.description}
              </p>
            </div>)}
        </div>

        <p className="font-default text-base leading-relaxed text-foreground/85 mt-10">
          Lightweight panels with strong thermal insulation. Precision-engineered for industrial durability and energy efficiency.
        </p>
        <div className="mt-10 flex items-baseline gap-4">
          <span className="font-default text-5xl md:text-6xl font-bold text-foreground">
            Price on Request
          </span>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Button className="w-full py-5 text-base bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-md">
            Request Quote
          </Button>
          <Button variant="outline" className="w-full py-5 text-base border-primary text-primary hover:bg-primary/5 rounded-lg">
            View Details →
          </Button>
        </div>
        <p className="mt-8 font-default italic text-sm text-primary">
          Direct sourcing from certified manufacturers.
        </p>
      </div>
    </div>;
}
export default function ProductDetails() {
  const wp_query_params = {
    per_page: 1,
    orderby: "date",
    order: "desc" as const
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2646" id="product-details" className="w-full bg-background text-foreground py-24 md:py-32 lg:py-[180px] px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <EcommerceProductsProvider wp_query={wp_query}>
          <FeaturedBladeConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}